/* eslint-disable tailwindcss/no-arbitrary-value */
import React, {
  useContext, useEffect, useRef, useState, useCallback, useMemo,
} from 'react';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import PropTypes from 'prop-types';
import {
  Button,
  Chip,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  IconButton,
} from '@one-thd/sui-atomic-components';
import {
  Add, Camera, Close, Document, Image, UpArrow
} from '@one-thd/sui-icons';
import { useAssistant, useAssistantStore } from '../../../orchestrator/AssistantOrchestrator';
import { shallow } from '../../../store/createStore';
import { useDrawer } from '../DrawerProvider';
import { FileUploadError } from './FileUploadError';
import {
  keyPressMap,
  MESSAGE_ORIGIN_CUSTOM,
} from '../../../shared/constants';
import { redactSensitiveContent, REDACTION_ERROR_MESSAGE } from '../../../shared/utils/redaction';
import { ScrollToBottomButton } from './ScrollToBottomButton';
import { BasicRoundSpinner } from '../../../shared/assets/BasicRoundSpinner';
import { ComposerInput } from '../ComposerInput';

/* eslint-disable max-len */
const ICON_BUTTON_BASE = 'sui-w-6 sui-h-6 sui-rounded-full sui-flex sui-items-center sui-justify-center sui-border-0 sui-transition-colors disabled:sui-opacity-40 disabled:sui-cursor-default';
const SUBMIT_BUTTON_CLASS = `${ICON_BUTTON_BASE} sui-bg-brand hover:enabled:sui-bg-brand-strong active:enabled:sui-bg-brand-strong sui-cursor-pointer`;
const UPLOAD_BUTTON_CLASS = `${ICON_BUTTON_BASE} sui-bg-transparent hover:enabled:sui-bg-subtle active:enabled:sui-bg-subtle-active sui-cursor-pointer`;
const INPUT_BOX_CLASS = 'sui-flex sui-flex-col sui-justify-between sui-items-center sui-border-1 sui-border-solid sui-border-primary sui-rounded-lg sui-overflow-hidden';
const IMAGE_REMOVE_BUTTON_CLASS = 'sui-group sui-relative sui-w-[70px] sui-h-[70px] sui-p-0 sui-border-0 sui-rounded-lg sui-cursor-pointer sui-bg-transparent hover:sui-outline hover:sui-outline-2 hover:-sui-outline-offset-2 hover:sui-outline-strong focus-visible:sui-outline focus-visible:sui-outline-4 focus-visible:-sui-outline-offset-4 focus-visible:sui-outline-input-focus';
const IMAGE_REMOVE_BADGE_CLASS = 'sui-absolute sui-top-1 sui-right-1 sui-w-6 sui-h-6 sui-rounded-full sui-bg-primary group-hover:sui-bg-strong sui-flex sui-items-center sui-justify-center sui-shadow-md';
const IMAGE_SKELETON_CLASS = 'sui-w-[70px] sui-h-[70px] sui-rounded-lg sui-bg-strong sui-flex sui-items-center sui-justify-center';
/* eslint-enable max-len */

export const DrawerInputWithFileUpload = () => {
  // Actions/handlers keep stable identity on the context object.
  const {
    askUserQuestion,
    setIsSubmitting,
    handleImageChange,
    clearImage,
    handleDocumentChange,
    clearDocument,
    setUploadError,
  } = useAssistant();
  // Reactive state comes from the store; one shallow-compared selector so this
  // input re-renders only when one of these fields actually changes.
  const {
    historyLoading,
    botLoading: botResponseLoading,
    isSubmitting,
    imageData,
    documentFile,
    isDocumentLoading,
    isLoading: isImageLoading,
    isEscalated,
    uploadError,
  } = useAssistantStore((state) => ({
    historyLoading: state.historyLoading,
    botLoading: state.botLoading,
    isSubmitting: state.isSubmitting,
    imageData: state.imageData,
    documentFile: state.documentFile,
    isDocumentLoading: state.isDocumentLoading,
    isLoading: state.isLoading,
    isEscalated: state.isEscalated,
    uploadError: state.uploadError,
  }), shallow);
  const { scrollToBottom, focusInput } = useDrawer();
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const [userMessage, setUserMessage] = useState('');
  const [isUploadDrawerOpen, setIsUploadDrawerOpen] = useState(false);
  const [redactionError, setRedactionError] = useState('');

  const isFileUploadEnabled = useConfigService('magic-apron-text-file-upload-enabled');
  const isUploadButtonEnabled = useConfigService('magic-apron-upload-button-enabled');

  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const textInputRef = useRef(null);
  const wasSubmittingRef = useRef(false);
  const submittedWithImageRef = useRef(false);

  // Keeps the callbacks below referentially stable — see
  // .claude/context/drawer-layout.md.
  const latest = useRef({});
  latest.current = {
    userMessage,
    imageData,
    botResponseLoading,
    historyLoading,
    isSubmitting,
    isDocumentLoading,
    isImageLoading,
    askUserQuestion,
    setIsSubmitting,
    setUploadError,
  };

  const clearPrompt = useCallback(() => {
    setUserMessage('');
    setRedactionError('');
  }, []);

  // React's autoFocus only fires on mount and cannot take focus options, so
  // focus is driven from the ref instead. focusInput={false} disables it entirely.
  useEffect(() => {
    if (!focusInput || !textInputRef.current) return;
    textInputRef.current.focus({ preventScroll: true });
  }, [focusInput]);

  // Taking the tap over ourselves is what keeps iOS from scrolling the page: the
  // browser's own focus scrolls the input into view, offsetting the visual viewport.
  const handleTouchEnd = useCallback((event) => {
    if (!textInputRef.current) return;
    event.preventDefault();
    textInputRef.current.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (wasSubmittingRef.current && !isSubmitting) {
      // preventScroll stops the browser scrolling the input into view, which on iOS
      // offsets the visual viewport and lets the page pan behind the drawer.
      if (textInputRef.current) textInputRef.current.focus({ preventScroll: true });
      submittedWithImageRef.current = false;
    }
    wasSubmittingRef.current = isSubmitting;
  }, [isSubmitting]);

  useEffect(() => {
    if (isImageLoading) {
      scrollToBottom();
    }
  }, [isImageLoading]);

  const handleImageUploadClick = () => {
    inputRef.current.click();
  };

  const handleCameraUploadClick = () => {
    cameraInputRef.current.click();
  };

  const handleFileUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleMessageSubmit = useCallback(async () => {
    const {
      userMessage: msg, imageData: image, botResponseLoading: loading, historyLoading: hLoading,
      isSubmitting: submitting, isDocumentLoading: docLoading, isImageLoading: imgLoading,
      askUserQuestion: ask, setIsSubmitting: setSubmitting, setUploadError: setErr,
    } = latest.current;
    const isBusy = loading || hLoading || submitting || docLoading || imgLoading;
    if ((msg.trim() === '' && !image) || isBusy) return;

    keyPressMap.Enter = false;
    setSubmitting(true);
    setErr(null);
    submittedWithImageRef.current = !!image;

    const submitted = await ask({
      userQuestion: msg,
      messageOrigin: MESSAGE_ORIGIN_CUSTOM,
    });
    if (submitted?.blocked) {
      setUserMessage(submitted.redacted);
      setRedactionError(submitted.error);
      return;
    }
    if (inputRef.current) inputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (submitted !== false) {
      clearPrompt();
    }
  }, [clearPrompt]);

  const onUserQuestionChange = useCallback((event) => {
    if (!(keyPressMap.Enter)) {
      const value = event.target.value;
      const { hasSensitive } = redactSensitiveContent(value);
      // Functional update keeps this callback dependency-free (stable). Returning
      // `prev` unchanged when it is already falsy avoids a pointless state update.
      setRedactionError((prev) => {
        if (hasSensitive) return REDACTION_ERROR_MESSAGE;
        return prev ? '' : prev;
      });
      setUserMessage(value);
    }
  }, []);

  const onUserQuestionKeyPress = useCallback((event) => {
    if (event.key === 'Enter' && event.type === 'keydown') {
      event.preventDefault();
      handleMessageSubmit();
    }
  }, [handleMessageSubmit]);

  const handleImageInputChange = (event) => {
    handleImageChange(event, 'image');
    setIsUploadDrawerOpen(false);
  };

  const handleCameraInputChange = (event) => {
    handleImageChange(event, 'camera');
    setIsUploadDrawerOpen(false);
  };

  const handleDocumentInputChange = (event) => {
    handleDocumentChange(event);
    setIsUploadDrawerOpen(false);
  };

  let inputValue = userMessage;
  if (isSubmitting && submittedWithImageRef.current) {
    inputValue = 'Submitting attachment...';
  } else if (isDocumentLoading || isImageLoading) {
    inputValue = 'Uploading attachment...';
  }

  // The input is inert (read-only + dimmed) while an attachment submit/upload is in flight.
  const isInputBusy = (isSubmitting && submittedWithImageRef.current)
    || isDocumentLoading || isImageLoading;

  const submitButton = useMemo(() => (
    <button
      type="button"
      onClick={handleMessageSubmit}
      disabled={isSubmitting || botResponseLoading || historyLoading || isDocumentLoading || isImageLoading}
      id="magic_apron_qa_find_it_for_me"
      key="submit-button"
      aria-label="Submit question"
      className={SUBMIT_BUTTON_CLASS}
    >
      <UpArrow color="inverse" size="small" />
    </button>
  ), [handleMessageSubmit, isSubmitting, botResponseLoading, historyLoading, isDocumentLoading, isImageLoading]);

  // Only shown inside the input adornment when the separate upload row is off.
  // Memoized so ComposerInput's memo isn't defeated by a fresh node each render.
  const trailingAdornment = useMemo(
    () => (isUploadButtonEnabled ? null : submitButton),
    [isUploadButtonEnabled, submitButton],
  );

  return (
    <DrawerFooter data-component="DrawerInputWithFileUpload">
      <ScrollToBottomButton />
      <input
        id="image-upload"
        onChange={handleImageInputChange}
        ref={inputRef}
        type="file"
        hidden
        accept=".jpg,.png,.jpeg"
      />
      <input
        id="camera-upload"
        onChange={handleCameraInputChange}
        ref={cameraInputRef}
        type="file"
        hidden
        accept="image/*"
        capture="environment"
      />
      <input
        id="file-upload"
        onChange={handleDocumentInputChange}
        ref={fileInputRef}
        type="file"
        hidden
        accept=".txt,.doc,.docx"
      />
      {uploadError && <FileUploadError errorMessage={uploadError} />}
      {redactionError && <FileUploadError errorMessage={REDACTION_ERROR_MESSAGE} />}
      <div data-testid="input-box" className={INPUT_BOX_CLASS}>
        <div className="sui-w-full [&_.sui-input-base-outline]:!sui-border-0">
          {(imageData || isImageLoading) && (
            <div className="sui-p-2 sui-flex sui-gap-2 sui-items-center sui-flex-wrap">
              {(imageData || isImageLoading) && (
                <div className="sui-relative sui-inline-block sui-w-[70px] sui-h-[70px]">
                  {isImageLoading ? (
                    <div className={IMAGE_SKELETON_CLASS}>
                      <BasicRoundSpinner />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { clearImage(); if (inputRef.current) inputRef.current.value = ''; }}
                      aria-label="Remove attachment"
                      className={IMAGE_REMOVE_BUTTON_CLASS}
                    >
                      <img
                        src={imageData.base64}
                        alt="uploaded preview"
                        width={70}
                        height={70}
                        className="sui-w-full sui-h-full sui-object-cover sui-rounded-md"
                      />
                      <span className={IMAGE_REMOVE_BADGE_CLASS}>
                        <Close size="small" />
                      </span>
                    </button>
                  )}
                </div>
              )}
              {(documentFile || isDocumentLoading) && (
                <Chip label={documentFile?.name ?? '...'} loading={isDocumentLoading} onClick={clearDocument} />
              )}
            </div>
          )}

          <ComposerInput
            value={inputValue}
            onChange={onUserQuestionChange}
            onKeyDown={onUserQuestionKeyPress}
            onKeyUp={onUserQuestionKeyPress}
            placeholder="Type a message..."
            ariaLabel={isEscalated ? 'Live agent textbox' : 'Assistant textbox'}
            inputRef={textInputRef}
            onTouchEnd={handleTouchEnd}
            readOnly={isInputBusy}
            dimmed={isInputBusy}
            showClear={!!userMessage.length}
            onClear={clearPrompt}
            trailingAdornment={trailingAdornment}
          />
        </div>
        {isUploadButtonEnabled && (
          <div className="sui-flex sui-justify-between sui-items-center sui-w-full sui-px-3 sui-py-2">
            <div className="sui-grow-0 sui-shrink-0">
              <button
                type="button"
                onClick={() => {
                  setUploadError(null);
                  if (!isMobile && !isFileUploadEnabled) {
                    handleImageUploadClick();
                  } else {
                    setIsUploadDrawerOpen(true);
                  }
                }}
                disabled={!!imageData || !!documentFile || isSubmitting || botResponseLoading}
                id="magic_apron_qa_upload_file"
                key="upload-file"
                aria-label="Upload file"
                className={UPLOAD_BUTTON_CLASS}
              >
                <Add color="medium" size="small" />
              </button>
            </div>
            <div className="sui-grow-0 sui-shrink-0">
              {submitButton}
            </div>
          </div>
        )}
      </div>
      <Drawer open={isUploadDrawerOpen} onOpenChange={() => {}}>
        <DrawerHeader
          title="Upload an Image"
          onClose={() => setIsUploadDrawerOpen(false)}
          endAdornment={(
            <IconButton aria-label="close" onClick={() => setIsUploadDrawerOpen(false)} icon={Close} />
          )}
        />
        <DrawerBody>
          <div className="sui-flex sui-gap-4 sm:sui-gap-4 sui-flex-col sm:sui-flex-row sui-w-full sm:sui-w-auto">
            <Button variant="tertiary" startIcon={Image} onClick={handleImageUploadClick} fullWidth>
              Image
            </Button>
            {isMobile && (
              <Button variant="tertiary" startIcon={Camera} onClick={handleCameraUploadClick} fullWidth>
                Camera
              </Button>
            )}
            {isFileUploadEnabled && (
              <Button variant="tertiary" startIcon={Document} onClick={handleFileUploadClick} fullWidth>
                File
              </Button>
            )}
          </div>
        </DrawerBody>
      </Drawer>
    </DrawerFooter>
  );
};

DrawerInputWithFileUpload.propTypes = {
};
